import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import ComerciosAdmin from './ComerciosAdmin';
import DescuentosAdmin from './DescuentosAdmin';
import AlertasAdmin from './AlertasAdmin';
import '../styles/AdminPage.css';

const TYPE_LABELS = {
  error: '🐛 Error',
  vulnerabilidad: '🔒 Vulnerabilidad',
  sugerencia: '💡 Sugerencia',
  otro: '📝 Otro',
};
const TYPE_BADGE = {
  error: 'badge-rojo',
  vulnerabilidad: 'badge-dorado',
  sugerencia: 'badge-verde',
  otro: 'badge-gris',
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toasts, success, error } = useToast();

  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [appReports, setAppReports] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [baseUrl, setBaseUrl] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatModal, setChatModal] = useState(null); // { report, messages, loading }

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/explorar'); return; }
    loadStats();
    loadUsers();
    loadReports();
    loadAppReports();
    loadInfluencers();
  }, [user]); // eslint-disable-line

  const loadStats = async () => {
    try { const d = await api.adminStats(); setStats(d); } catch {}
  };

  const recordarFoto = async () => {
    const n = stats?.fotos?.paraRecordar || 0;
    if (!window.confirm(`¿Mandar el correo para completar el perfil a ${n} usuario${n === 1 ? '' : 's'} del plan gratis?${stats?.promoActiva ? `\n\nLes contamos que con el perfil completo y la foto ganan ${stats.promoDias} días de Premium (quedan ${stats.fundadoresRestantes} lugares).` : ''}`)) return;
    try { const r = await api.adminRecordarFoto(); success(r.message); loadStats(); }
    catch (e) { error(e.message); }
  };

  // Renueva la sesión antes de bajar el archivo (la descarga va directo, sin pasar por api.js)
  const descargarRespaldo = async () => {
    try {
      await api.adminStats();
      window.location.href = '/api/admin/respaldo/descargar';
      success('Armando el respaldo, la descarga empieza en unos segundos.');
    } catch { error('No se pudo iniciar la descarga. Volvé a iniciar sesión.'); }
  };

  const loadUsers = async (q = '') => {
    setLoading(true);
    try { const d = await api.adminUsers(q, 1); setUsers(d.users || []); }
    catch { error('Error cargando usuarios.'); }
    finally { setLoading(false); }
  };

  const loadReports = async () => {
    try { const d = await api.adminReports(); setReports(d.reports || []); } catch {}
  };

  const loadAppReports = async () => {
    try { const d = await api.adminAppReports(); setAppReports(d.appReports || []); } catch {}
  };

  const loadInfluencers = async () => {
    try {
      const d = await api.adminInfluencers();
      setInfluencers(d.influencers || []);
      setBaseUrl(d.baseUrl || window.location.origin);
    } catch {}
  };

  const refLink = (code) => `${baseUrl || window.location.origin}/?ref=${code}`;

  const copyLink = async (code) => {
    try { await navigator.clipboard.writeText(refLink(code)); success('Enlace copiado.'); }
    catch { window.prompt('Copiá el enlace:', refLink(code)); }
  };

  const reloadAfterPlanChange = () => { loadUsers(search); loadInfluencers(); loadStats(); };

  const handleCambiarEmail = async (u) => {
    const nuevo = window.prompt(`Correo de ${u.name}`, u.email || '');
    if (nuevo === null || !nuevo.trim() || nuevo.trim() === u.email) return;
    try { const r = await api.adminCambiarEmail(u.id, nuevo.trim()); success(r.message); loadUsers(search); }
    catch (e) { error(e.message); }
  };

  const handleGrantInfluencer = async (u) => {
    const months = window.prompt(`Plan Influencer para ${u.name}\n\n¿Cuántos meses?`, '6');
    if (months === null) return;
    const n = parseInt(months, 10);
    if (!n || n < 1 || n > 24) { error('Meses inválidos (1 a 24).'); return; }
    const code = window.prompt('Código para su enlace (vacío = se genera con el nombre).\nSolo minúsculas, números, - o _', u.ref_code || '');
    if (code === null) return;
    try {
      const r = await api.adminInfluencer(u.id, { action: 'grant', months: n, refCode: code.trim() || undefined });
      success(`Plan Influencer asignado. Enlace: ${refLink(r.refCode)}`);
      reloadAfterPlanChange();
    } catch (e) { error(e.message); }
  };

  const handleExtendInfluencer = async (u) => {
    const months = window.prompt(`¿Cuántos meses sumarle a ${u.name}?`, '3');
    if (months === null) return;
    const n = parseInt(months, 10);
    if (!n || n < 1 || n > 24) { error('Meses inválidos (1 a 24).'); return; }
    try { await api.adminInfluencer(u.id, { action: 'extend', months: n }); success('Plan extendido.'); reloadAfterPlanChange(); }
    catch (e) { error(e.message); }
  };

  const handleRevokeInfluencer = async (u) => {
    if (!window.confirm(`¿Quitarle el plan Influencer a ${u.name}? Pierde el premium y la insignia VIP.`)) return;
    try { await api.adminInfluencer(u.id, { action: 'revoke' }); success('Plan Influencer quitado.'); reloadAfterPlanChange(); }
    catch (e) { error(e.message); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-AR') : '—';

  const handleSearch = (e) => {
    setSearch(e.target.value);
    loadUsers(e.target.value);
  };

  const handleBan = async (id, status) => {
    try {
      await api.adminSetStatus(id, status === 'active' ? 'banned' : 'active', '');
      success(status === 'active' ? 'Usuario suspendido.' : 'Usuario reactivado.');
      loadUsers(search);
    } catch (e) { error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar permanentemente?')) return;
    try { await api.adminDeleteUser(id); success('Usuario eliminado.'); loadUsers(search); }
    catch (e) { error(e.message); }
  };

  const openChatModal = async (report) => {
    setChatModal({ report, messages: [], loading: true });
    try {
      const data = await api.adminChatBetween(report.reporter_id || report.id, report.reported_id);
      setChatModal({ report, messages: data.messages || [], loading: false, hasMatch: data.hasMatch });
    } catch {
      setChatModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleResolveReport = async (id, status) => {
    try {
      await api.adminResolveReport(id, status, '');
      success(status === 'reviewed' ? 'Usuario baneado.' : 'Reporte desestimado.');
      loadReports();
      loadStats();
      setChatModal(null);
    } catch (e) { error(e.message); }
  };

  const handleResolveAppReport = async (id, status) => {
    try { await api.adminResolveAppReport(id, status, ''); success('Reporte de app actualizado.'); loadAppReports(); loadStats(); }
    catch (e) { error(e.message); }
  };

  const pendingAppReports = appReports.filter(r => r.status === 'pending').length;
  const pendingUserReports = reports.filter(r => r.status === 'pending').length;

  return (
    <div className="admin-page">
      <Navbar />
      <ToastContainer toasts={toasts} />

      <div className="admin-container container">
        <div className="admin-header">
          <h1 className="admin-title">🛡️ Panel de Administración</h1>
          <p className="admin-sub">Cupido con Mate — San Juan</p>
        </div>

        {/* STATS CARDS */}
        {stats && (
          <div className="admin-stats">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-value">{stats.totalUsuarios}</div>
              <div className="stat-label">Usuarios activos</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💕</div>
              <div className="stat-value">{stats.totalMatches}</div>
              <div className="stat-label">Conexiones totales</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-value">${stats.revenueEstimado?.toLocaleString('es-AR')}</div>
              <div className="stat-label">Ingreso estimado/mes</div>
            </div>
            <div className="stat-card" style={pendingUserReports > 0 ? { borderColor: 'var(--rojo)', background: 'rgba(230,57,70,0.05)' } : {}}>
              <div className="stat-icon">⚠️</div>
              <div className="stat-value">{stats.reportesPendientes || 0}</div>
              <div className="stat-label">Reportes de usuarios</div>
            </div>
            <div className="stat-card" style={stats.appReportesPendientes > 0 ? { borderColor: 'var(--dorado)', background: 'rgba(244,162,97,0.05)' } : {}}>
              <div className="stat-icon">🐛</div>
              <div className="stat-value">{stats.appReportesPendientes || 0}</div>
              <div className="stat-label">Reportes de app</div>
            </div>
          </div>
        )}

        {/* TABS */}
        <div className="admin-tabs">
          <button className={`admin-tab ${tab === 'stats' ? 'active' : ''}`} onClick={() => setTab('stats')}>
            📊 Estadísticas
          </button>
          <button className={`admin-tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
            👥 Usuarios
          </button>
          <button className={`admin-tab ${tab === 'influencers' ? 'active' : ''}`} onClick={() => setTab('influencers')}>
            👑 Influencers
          </button>
          <button className={`admin-tab ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}>
            ⚠️ Usuarios {pendingUserReports > 0 && <span className="tab-badge">{pendingUserReports}</span>}
          </button>
          <button className={`admin-tab ${tab === 'app-reports' ? 'active' : ''}`} onClick={() => setTab('app-reports')}>
            🐛 App {pendingAppReports > 0 && <span className="tab-badge">{pendingAppReports}</span>}
          </button>
          <button className={`admin-tab ${tab === 'comercios' ? 'active' : ''}`} onClick={() => setTab('comercios')}>
            🛵 Delivery
          </button>
          <button className={`admin-tab ${tab === 'descuentos' ? 'active' : ''}`} onClick={() => setTab('descuentos')}>
            🏷️ Descuentos
          </button>
          <button className={`admin-tab ${tab === 'alertas' ? 'active' : ''}`} onClick={() => setTab('alertas')}>
            🚨 Alertas
          </button>
        </div>

        {/* COMERCIOS TAB */}
        {tab === 'comercios' && <ComerciosAdmin success={success} error={error} />}
        {tab === 'descuentos' && <DescuentosAdmin success={success} error={error} />}
        {tab === 'alertas' && <AlertasAdmin success={success} error={error} />}

        {/* STATS TAB */}
        {tab === 'stats' && stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { label: 'Nuevos hoy', value: stats.usuariosHoy || 0, icon: '🆕' },
              { label: 'Conexiones hoy', value: stats.matchesHoy || 0, icon: '💘' },
              { label: 'Mensajes hoy', value: stats.mensajesHoy || 0, icon: '💬' },
              { label: 'Plan Mate ☕', value: stats.planMate || 0, icon: '☕' },
              { label: 'Plan Premium 🧉', value: stats.planPremium || 0, icon: '🧉' },
              { label: `Fundadores (quedan ${stats.fundadoresRestantes ?? '-'})`, value: stats.fundadores || 0, icon: '👑' },
              { label: 'Influencers activos', value: stats.influencers || 0, icon: '👑' },
              { label: 'Registros por enlaces', value: stats.referidosTotal || 0, icon: '🔗' },
              { label: 'Usuarios baneados', value: stats.usuariosBaneados || 0, icon: '🚫' },
            ].map(s => (
              <div key={s.label} className="stat-card">
                <div className="stat-icon">{s.icon}</div>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
            <div className="stat-card">
              <div className="stat-icon">📸</div>
              <div className="stat-value">{stats.fotos?.incompletos || 0}</div>
              <div className="stat-label">Plan gratis sin foto o con perfil incompleto</div>
              <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} disabled={!stats.fotos?.paraRecordar} onClick={recordarFoto}>
                Mandarles el correo{stats.fotos?.paraRecordar ? ` (${stats.fotos.paraRecordar})` : ''}
              </button>
              <div className="stat-label" style={{ marginTop: 8 }}>
                {stats.promoActiva ? `Les llega un correo: si completan el perfil con foto ganan ${stats.promoDias} días de Premium gratis.` : 'Les llega un correo invitándolos a completar el perfil.'} Como mucho uno cada 7 días.
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💾</div>
              <button className="btn btn-primary" onClick={descargarRespaldo}>Descargar respaldo</button>
              <div className="stat-label" style={{ marginTop: 8 }}>
                Base de datos y fotos en un archivo .tar.gz. Tiene datos personales: guardalo en un lugar seguro.
              </div>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {tab === 'users' && (
          <>
            <div className="admin-search">
              <input className="form-input" type="text" placeholder="Buscar por nombre o correo..."
                value={search} onChange={handleSearch} />
            </div>
            <div className="users-table-wrap">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Plan</th>
                    <th>Estado</th>
                    <th>Registro</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--texto-muted)' }}>Cargando...</td></tr>
                  ) : users.map(u => (
                    <tr key={u.id} className={u.status === 'banned' ? 'banned-row' : ''}>
                      <td>
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            {u.foto_path ? <img src={u.foto_path} alt={u.name} /> : <span>{(u.name||'U')[0].toUpperCase()}</span>}
                          </div>
                          <div>
                            <div className="user-cell-name">{u.name}</div>
                            <div className="user-cell-email">{u.email}</div>
                            {u.telefono && <div className="user-cell-email">Tel: {u.telefono.replace('-', ' - ')}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${u.is_influencer || u.plan === 'premium' ? 'badge-dorado' : u.plan === 'mate' ? 'badge-rojo' : 'badge-gris'}`}>
                          {u.is_influencer ? '👑 influencer' : { free: 'gratis', mate: 'mate', premium: 'premium' }[u.plan] || u.plan}
                        </span>
                        {u.plan !== 'free' && u.plan_expires_at && (
                          <div style={{ fontSize: 11, color: 'var(--texto-muted)', marginTop: 4 }}>vence {fmtDate(u.plan_expires_at)}</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${u.status === 'active' ? 'badge-verde' : 'badge-rojo'}`}>
                          {{ active: 'activo', banned: 'suspendido', blocked: 'bloqueado', pending: 'pendiente' }[u.status] || u.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--texto-muted)' }}>
                        {new Date(u.created_at).toLocaleDateString('es-AR')}
                      </td>
                      <td>
                        <div className="admin-actions">
                          <button className="btn btn-sm btn-outline" onClick={() => handleBan(u.id, u.status)}>
                            {u.status === 'active' ? '🚫 Suspender' : '✅ Activar'}
                          </button>
                          <button className="btn btn-sm btn-outline" title="Corregir correo" onClick={() => handleCambiarEmail(u)}>✏️ Correo</button>
                          {u.is_influencer ? (
                            <button className="btn btn-sm btn-outline" onClick={() => handleRevokeInfluencer(u)}>Quitar VIP</button>
                          ) : (
                            <button className="btn btn-sm btn-outline" onClick={() => handleGrantInfluencer(u)}>👑 Influencer</button>
                          )}
                          <button className="btn btn-sm"
                            style={{ background: 'rgba(230,57,70,0.2)', color: 'var(--rojo-claro)', border: '1px solid rgba(230,57,70,0.3)' }}
                            onClick={() => handleDelete(u.id)}>
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* INFLUENCERS TAB */}
        {tab === 'influencers' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--texto-suave)', marginBottom: 12 }}>
              Para sumar un influencer, buscalo en la pestaña Usuarios (tiene que estar registrado) y tocá "👑 Influencer".
              Válido = perfil completo con foto, email verificado y siguió entrando 7 días después de registrarse.
            </p>
            <div className="users-table-wrap">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Influencer</th>
                    <th>Link</th>
                    <th>Vence</th>
                    <th>Registros</th>
                    <th>Completos</th>
                    <th>Válidos</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {influencers.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: 'var(--texto-muted)' }}>Todavía no hay influencers.</td></tr>
                  ) : influencers.map(i => (
                    <tr key={i.id} style={i.vigente ? {} : { opacity: 0.55 }}>
                      <td>
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            {i.foto_path ? <img src={i.foto_path} alt={i.name} /> : <span>{(i.name||'U')[0].toUpperCase()}</span>}
                          </div>
                          <div>
                            <div className="user-cell-name">{i.name} {i.vigente ? '👑' : '(vencido)'}</div>
                            <div className="user-cell-email">{i.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline" onClick={() => copyLink(i.ref_code)} title={refLink(i.ref_code)}>
                          📋 ?ref={i.ref_code}
                        </button>
                      </td>
                      <td style={{ fontSize: 12 }}>{i.vigente ? fmtDate(i.plan_expires_at) : '—'}</td>
                      <td><strong>{i.referidos}</strong></td>
                      <td>{i.completos}</td>
                      <td><strong style={{ color: 'var(--dorado)' }}>{i.validos}</strong></td>
                      <td>
                        <div className="admin-actions">
                          {i.vigente ? (
                            <>
                              <button className="btn btn-sm btn-outline" onClick={() => handleExtendInfluencer(i)}>+ meses</button>
                              <button className="btn btn-sm btn-outline" onClick={() => handleRevokeInfluencer(i)}>Quitar</button>
                            </>
                          ) : (
                            <button className="btn btn-sm btn-outline" onClick={() => handleGrantInfluencer(i)}>Renovar</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* USER REPORTS TAB */}
        {tab === 'reports' && (
          <div className="reports-list">
            {reports.length === 0 ? (
              <div className="no-reports">✅ No hay reportes de usuarios pendientes</div>
            ) : reports.map(r => (
              <div key={r.id} className="report-card">
                <div className="report-card-header">
                  <span className={`badge ${r.status === 'pending' ? 'badge-rojo' : 'badge-gris'}`}>
                    {r.status === 'pending' ? '⚠️ Pendiente' : r.status}
                  </span>
                  <span className="report-date">{new Date(r.created_at).toLocaleDateString('es-AR')}</span>
                </div>
                <div className="report-details">
                  <div className="report-item"><span>Reportado</span><span>{r.reported_name || r.reported_id}</span></div>
                  <div className="report-item"><span>Por</span><span>{r.reporter_name || r.reporter_id}</span></div>
                  {r.reason && <div className="report-item"><span>Motivo</span><span>{r.reason}</span></div>}
                </div>
                {r.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => handleResolveReport(r.id, 'dismissed')}>Desestimar</button>
                    <button className="btn btn-sm btn-primary" onClick={() => openChatModal(r)}>👁️ Ver chat + Banear</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* APP REPORTS TAB */}
        {tab === 'app-reports' && (
          <div className="reports-list">
            {appReports.length === 0 ? (
              <div className="no-reports">✅ No hay reportes de app</div>
            ) : appReports.map(r => (
              <div key={r.id} className={`report-card ${r.type === 'vulnerabilidad' ? 'report-card--vuln' : ''}`}>
                <div className="report-card-header">
                  <span className={`badge ${TYPE_BADGE[r.type] || 'badge-gris'}`}>
                    {TYPE_LABELS[r.type] || r.type}
                  </span>
                  <span className={`badge ${r.status === 'pending' ? 'badge-rojo' : r.status === 'reviewed' ? 'badge-dorado' : 'badge-verde'}`}>
                    {r.status === 'pending' ? 'Pendiente' : r.status === 'reviewed' ? 'Revisado' : 'Resuelto'}
                  </span>
                  <span className="report-date">{new Date(r.created_at).toLocaleDateString('es-AR')}</span>
                </div>
                <div className="report-details">
                  <div className="report-item"><span>Usuario</span><span>{r.user_name} ({r.user_email}) — plan {r.user_plan}</span></div>
                  {r.url_context && <div className="report-item"><span>Página</span><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.url_context}</span></div>}
                  <div className="report-description">{r.description}</div>
                </div>
                {r.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => handleResolveAppReport(r.id, 'reviewed')}>
                      👁️ Marcar revisado
                    </button>
                    <button className="btn btn-sm btn-primary" onClick={() => handleResolveAppReport(r.id, 'resolved')}>
                      ✅ Marcar resuelto
                    </button>
                  </div>
                )}
                {r.status === 'reviewed' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => handleResolveAppReport(r.id, 'resolved')}>
                      ✅ Marcar resuelto
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CHAT LOG MODAL */}
      {chatModal && (
        <div className="modal-overlay" onClick={() => setChatModal(null)}>
          <div className="modal" style={{ maxWidth: 540, width: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, margin: 0 }}>
                🗨️ Chat: {chatModal.report.reporter_name} → {chatModal.report.reported_name}
              </h2>
              <button className="btn btn-sm btn-outline" onClick={() => setChatModal(null)}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--texto-suave)', marginBottom: 8 }}>
              Motivo del reporte: <strong>{chatModal.report.reason || 'Sin especificar'}</strong>
            </p>

            <div style={{ flex: 1, overflowY: 'auto', background: 'var(--fondo)', borderRadius: 12, padding: '12px 16px', minHeight: 120, maxHeight: 400, marginBottom: 16 }}>
              {chatModal.loading ? (
                <p style={{ textAlign: 'center', color: 'var(--texto-muted)', padding: 20 }}>Cargando conversación...</p>
              ) : !chatModal.hasMatch || chatModal.messages.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--texto-muted)', padding: 20 }}>
                  {chatModal.hasMatch ? 'No hay mensajes en esta conversación.' : 'Estos usuarios no tienen conexión entre sí.'}
                </p>
              ) : (
                chatModal.messages.map(msg => (
                  <div key={msg.id} style={{ marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--primario)' }}>{msg.sender_name}</span>
                    <span style={{ fontSize: 11, color: 'var(--texto-muted)', marginLeft: 8 }}>
                      {new Date(msg.created_at).toLocaleString('es-AR')}
                    </span>
                    <div style={{ background: 'var(--superficie)', borderRadius: 8, padding: '6px 10px', marginTop: 4, fontSize: 14 }}>
                      {msg.text}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setChatModal(null)}>Cancelar</button>
              <button className="btn btn-outline" onClick={() => handleResolveReport(chatModal.report.id, 'dismissed')}>
                Desestimar reporte
              </button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--rojo, #e53935)' }}
                onClick={() => handleResolveReport(chatModal.report.id, 'reviewed')}
              >
                🚫 Banear usuario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
